import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HOME_ROUTE } from '@/config/routes';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { NavigationGuardProvider } from '@/contexts/NavigationGuardContext';
import { FeatureFlagProvider, FeatureGate, DevFlagPanel } from '@/features/flags';
import MainLayout from '@/components/layout/MainLayout';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
// DH1.2 — demo-variant badge; the static env gate at the mount point lets
// Rollup drop the component (and this import) from real-API builds entirely.
import DemoRibbon from '@/components/common/DemoRibbon';
// S10.1 — GovernancePage evolved into the 系統 System pages (組織/角色/用戶/稽核).
import SystemGovernancePage from '@/components/system/SystemGovernancePage';
// CC-Claims — codex ClaimsPage rebuilt on the house list stack, split per-page.
import ClaimsListPage from '@/components/claims/ClaimsListPage';
import ClaimCreatePage from '@/components/claims/ClaimCreatePage';
import ClaimDetailPage from '@/components/claims/ClaimDetailPage';

// Vendor Pages
import VendorListPage from '@/app/auth/vendors';
import VendorDetailPage from '@/app/auth/vendors/$vendorId';
import VendorAddPage from '@/app/auth/vendors/add';
import VendorEditPage from '@/app/auth/vendors/$vendorId/edit';

// Customer Pages
import CustomerListPage from '@/app/auth/customers';
import CustomerDetailPage from '@/app/auth/customers/$customerId';
import CustomerAddPage from '@/app/auth/customers/add';
import CustomerEditPage from '@/app/auth/customers/$customerId/edit';

// Category Pages
import CategoryListPage from '@/components/categories/CategoryListPage';
import CategoryDetailPage from '@/components/categories/CategoryDetailPage';

// Contact Pages
import ContactListPage from '@/components/contacts/ContactListPage';
import ContactDetailPage from '@/components/contacts/ContactDetailPage';

// Wave 1 — Products + taxonomy
import ProductListPage from '@/components/products/ProductListPage';
import ProductFormWizard from '@/components/products/ProductFormWizard';
import ProductDetailPage from '@/components/products/ProductDetailPage';
import TaxonomyAdminPage from '@/components/taxonomy/TaxonomyAdminPage';

// Wave 7 (S7.2) — SCN Spec Master (the one net-new surface)
import ScnSpecListPage from '@/components/scn/ScnSpecListPage';
import ScnSpecDetailPage from '@/components/scn/ScnSpecDetailPage';

// Wave 2 — Orders / OCC
import SalesOrderListPage from '@/components/salesOrders/SalesOrderListPage';
import SalesOrderCreatePage from '@/components/salesOrders/SalesOrderCreatePage';
import SalesOrderDetailPage from '@/components/salesOrders/SalesOrderDetailPage';
import SalesOrderEditPage from '@/components/salesOrders/SalesOrderEditPage';
// Wave 3 (S3.2) — procurement rebuilt on the house stack
import PurchaseOrderListPage from '@/components/procurement/PurchaseOrderListPage';
import PurchaseOrderDetailPage from '@/components/procurement/PurchaseOrderDetailPage';
import DemandPoolPage from '@/components/procurement/DemandPoolPage';
import OccBoardPage from '@/components/occ/OccBoardPage';
// Wave 6 (S6.1) — 船務 split onto the house list stack (GR moved to W5)
import ShippingNoticePage from '@/components/shipping/ShippingNoticePage';
import ShipmentBatchPage from '@/components/shipping/ShipmentBatchPage';
// Wave 5 (S5.1) — 進貨 rebuilt under Procurement (Board E pink placement)
import GoodsReceiptPage from '@/components/procurement/GoodsReceiptPage';
import ReceiptInvoiceMatchView from '@/components/finance/ReceiptInvoiceMatchView';

// Wave 8 (S8.1) — AP/AR/GL split out of FinancePages onto the house stack
import ApPage from '@/components/finance/ApPage';
import ArPage from '@/components/finance/ArPage';
import GlPage from '@/components/finance/GlPage';
// Wave 7 (S7.1) — invoices out of FinancePages onto the house stack
import InvoiceListPage from '@/components/finance/InvoiceListPage';
import InvoiceDetailPage from '@/components/finance/InvoiceDetailPage';
import { CostingEnginePage } from '@/components/engines/EnginesPages';
// Wave 9 (S9.1) — 定價設定 on the house stack (route-derived tabs); the codex
// EnginesPages pricing fixture page is deleted, its /auth/engines/pricing URL
// redirects here so the 系統 nav item keeps working until it is repointed.
import PriceSetupPage from '@/components/pricing/PriceSetupPage';
// Wave 9 (S9.2) — standalone 價格模擬器 reusing the shared resolve endpoint
import PriceSimulatorPage from '@/components/pricing/PriceSimulatorPage';
// Wave 11 (S11.1) — the five pink reports on a route-derived tab shell
// (/auth/reports/:report). The 報表 nav wires all five (navigation.constants.ts);
// an unknown key falls back to the landing report.
import ReportsPage from '@/components/reports/ReportsPage';

// Auth Pages
import LoginPage from '@/app/login';

function App() {
  return (
    <ThemeProvider defaultMode="light">
      <FeatureFlagProvider>
        <NavigationGuardProvider>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <Routes>
              {/* Login page - outside main layout */}
              <Route path="/login" element={<LoginPage />} />

              {/* Redirect root to vendors */}
              <Route path="/" element={<Navigate to={HOME_ROUTE} replace />} />

              {/* Protected routes with layout */}
              <Route
                path="/auth"
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                {/* S10.1: 系統與權限 tabs are ROUTE-DERIVED
                    (/auth/governance/:tab, tab = organisations|roles|users|audit).
                    Prefix stays /auth/governance so the nav item + the
                    routePermissions `governance.manage` gate keep working. */}
                <Route
                  path="governance"
                  element={
                    <FeatureGate flag="governance.core">
                      <Navigate to="/auth/governance/organisations" replace />
                    </FeatureGate>
                  }
                />
                <Route
                  path="governance/:tab"
                  element={
                    <FeatureGate flag="governance.core">
                      <SystemGovernancePage />
                    </FeatureGate>
                  }
                />
                {/* Vendor routes */}
                <Route path="vendors" element={<VendorListPage />} />
                <Route path="vendors/add" element={<VendorAddPage />} />
                <Route path="vendors/:vendorId/edit" element={<VendorEditPage />} />
                <Route path="vendors/:vendorId" element={<VendorDetailPage />} />

                {/* Customer routes */}
                <Route path="customers" element={<CustomerListPage />} />
                <Route path="customers/add" element={<CustomerAddPage />} />
                <Route path="customers/:customerId/edit" element={<CustomerEditPage />} />
                <Route path="customers/:customerId" element={<CustomerDetailPage />} />

                {/* Category routes */}
                <Route path="categories" element={<CategoryListPage />} />
                <Route path="categories/:id" element={<CategoryDetailPage />} />

                {/* Contact routes */}
                <Route path="contacts" element={<ContactListPage />} />
                <Route path="contacts/:id" element={<ContactDetailPage />} />

                {/* Wave 1: Products + taxonomy */}
                <Route
                  path="products"
                  element={
                    <FeatureGate flag="mdm.products">
                      <ProductListPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="products/add"
                  element={
                    <FeatureGate flag="mdm.products">
                      <ProductFormWizard />
                    </FeatureGate>
                  }
                />
                {/* S7.2 — SCN 共同規格主檔 (Spec Master). Declared BEFORE the
                    products/:productId routes for readability; React Router
                    ranks the static `scn` segment above the dynamic param
                    either way. Rides the existing mdm.products flag (FD10) and
                    the /auth/products routePermissions prefix (products.manage
                    — Frame 274「SCN 要開權限」, build-default Q-O1). */}
                <Route
                  path="products/scn"
                  element={
                    <FeatureGate flag="mdm.products">
                      <ScnSpecListPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="products/scn/:scn"
                  element={
                    <FeatureGate flag="mdm.products">
                      <ScnSpecDetailPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="products/:productId/edit"
                  element={
                    <FeatureGate flag="mdm.products">
                      <ProductFormWizard />
                    </FeatureGate>
                  }
                />
                <Route
                  path="products/:productId"
                  element={
                    <FeatureGate flag="mdm.products">
                      <ProductDetailPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="product-taxonomy"
                  element={
                    <FeatureGate flag="mdm.productTaxonomy">
                      <TaxonomyAdminPage />
                    </FeatureGate>
                  }
                />

                {/* Wave 2: SO / PO / OCC / shipping */}
                <Route
                  path="sales-orders"
                  element={
                    <FeatureGate flag="sales.orders">
                      <SalesOrderListPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="sales-orders/add"
                  element={
                    <FeatureGate flag="sales.orders">
                      <SalesOrderCreatePage />
                    </FeatureGate>
                  }
                />
                {/* CC-Claims: list / create / detail (static `add` out-ranks
                    the :claimId param in React Router). Behind sales.claims. */}
                <Route
                  path="claims"
                  element={
                    <FeatureGate flag="sales.claims">
                      <ClaimsListPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="claims/add"
                  element={
                    <FeatureGate flag="sales.claims">
                      <ClaimCreatePage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="claims/:claimId"
                  element={
                    <FeatureGate flag="sales.claims">
                      <ClaimDetailPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="sales-orders/:soId/edit"
                  element={
                    <FeatureGate flag="sales.orders">
                      <SalesOrderEditPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="sales-orders/:soId"
                  element={
                    <FeatureGate flag="sales.orders">
                      <SalesOrderDetailPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="purchase-orders"
                  element={
                    <FeatureGate flag="procurement.po">
                      <PurchaseOrderListPage />
                    </FeatureGate>
                  }
                />
                {/* Static segment beats :poId (React Router ranking). The pool
                    is a route-derived lens surface (:lens = shortage | reorder,
                    SR3.1); the bare path defaults to the 訂單短缺 shortage lens so
                    existing deep-links keep landing. The 補貨建議 reorder lens is
                    gated inside the page on reorder.suggestions (flag-off = zero
                    new UI); the page itself rides procurement.poFromSo. */}
                <Route
                  path="purchase-orders/demand-pool"
                  element={
                    <FeatureGate flag="procurement.poFromSo">
                      <DemandPoolPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="purchase-orders/demand-pool/:lens"
                  element={
                    <FeatureGate flag="procurement.poFromSo">
                      <DemandPoolPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="purchase-orders/:poId"
                  element={
                    <FeatureGate flag="procurement.po">
                      <PurchaseOrderDetailPage />
                    </FeatureGate>
                  }
                />
                {/* S4.3: lens tabs are route-derived (/auth/occ/:lens);
                    landing = 訂單進度總覽 (overview) */}
                <Route
                  path="occ"
                  element={
                    <FeatureGate flag="occ.board">
                      <Navigate to="/auth/occ/overview" replace />
                    </FeatureGate>
                  }
                />
                <Route
                  path="occ/:lens"
                  element={
                    <FeatureGate flag="occ.board">
                      <OccBoardPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="shipments"
                  element={
                    <FeatureGate flag="shipping.batch">
                      <ShipmentBatchPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="shipping-notices"
                  element={
                    <FeatureGate flag="shipping.notice">
                      <ShippingNoticePage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="goods-receipts"
                  element={
                    <FeatureGate flag="fulfillment.receive">
                      <GoodsReceiptPage />
                    </FeatureGate>
                  }
                />
                {/* S5.1 會計對帳 — receipt vs vendor invoice match (W5
                    deliverable, so it rides the goods-receipt flag, not the
                    W7/W8 finance flags) */}
                <Route
                  path="finance/receipt-match"
                  element={
                    <FeatureGate flag="fulfillment.receive">
                      <ReceiptInvoiceMatchView />
                    </FeatureGate>
                  }
                />

                {/* Wave 7 (S7.1): invoices — kind tabs are route-derived
                    (/auth/finance/invoices/:kind, customer|vendor). Gated on
                    finance.ar until a dedicated finance.invoices flag lands
                    (featureFlags.ts is outside the S7.1 file set). */}
                <Route
                  path="finance/invoices"
                  element={
                    <FeatureGate flag="finance.ar">
                      <Navigate to="/auth/finance/invoices/customer" replace />
                    </FeatureGate>
                  }
                />
                <Route
                  path="finance/invoices/:kind"
                  element={
                    <FeatureGate flag="finance.ar">
                      <InvoiceListPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="finance/invoices/:kind/:invoiceId"
                  element={
                    <FeatureGate flag="finance.ar">
                      <InvoiceDetailPage />
                    </FeatureGate>
                  }
                />

                {/* Wave 8 (S8.1): AP view tabs are route-derived
                    (/auth/finance/ap/:view, invoices|payments 沖帳紀錄) */}
                <Route
                  path="finance/ap"
                  element={
                    <FeatureGate flag="finance.ap">
                      <Navigate to="/auth/finance/ap/invoices" replace />
                    </FeatureGate>
                  }
                />
                <Route
                  path="finance/ap/:view"
                  element={
                    <FeatureGate flag="finance.ap">
                      <ApPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="finance/ar"
                  element={
                    <FeatureGate flag="finance.ar">
                      <ArPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="finance/gl"
                  element={
                    <FeatureGate flag="finance.glStatements">
                      <GlPage />
                    </FeatureGate>
                  }
                />
                {/* S9.1: 定價設定 price setup — tabs are ROUTE-DERIVED
                    (/auth/pricing/setup/:tab, matrix|overrides|cost-basis).
                    Rides the engines.pricing flag (navigation.constants.ts
                    gates the 系統 nav item on it; a rename to a pricing.setup
                    flag needs featureFlags.ts — outside the S9.1 file set). */}
                <Route
                  path="pricing/setup"
                  element={
                    <FeatureGate flag="engines.pricing">
                      <Navigate to="/auth/pricing/setup/matrix" replace />
                    </FeatureGate>
                  }
                />
                <Route
                  path="pricing/setup/:tab"
                  element={
                    <FeatureGate flag="engines.pricing">
                      <PriceSetupPage />
                    </FeatureGate>
                  }
                />
                {/* S9.2: 價格模擬器 — same resolve endpoint as SO entry, so
                    simulator == order-entry price for identical inputs. Rides
                    engines.pricing like the setup screens (nav item repoint is
                    navigation.constants.ts — another slice's file, follow-up
                    recorded). */}
                <Route
                  path="pricing/simulator"
                  element={
                    <FeatureGate flag="engines.pricing">
                      <PriceSimulatorPage />
                    </FeatureGate>
                  }
                />
                {/* Legacy URL of the deleted EnginesPages pricing fixture page —
                    kept so old links survive; the 系統 nav now points straight
                    at /auth/pricing/setup (navigation.constants.ts). */}
                <Route
                  path="engines/pricing"
                  element={
                    <FeatureGate flag="engines.pricing">
                      <Navigate to="/auth/pricing/setup/matrix" replace />
                    </FeatureGate>
                  }
                />
                {/* Costing fixture viewer — DEV-ONLY (matches its DevFlagPanel
                    card in GlPage: `import.meta.env.DEV && flags['engines.costing']`).
                    In a production build the route resolves to the fallback, so
                    the fixture is never reachable on a business URL. */}
                <Route
                  path="engines/costing"
                  element={
                    import.meta.env.DEV ? (
                      <FeatureGate flag="engines.costing">
                        <CostingEnginePage />
                      </FeatureGate>
                    ) : (
                      <Navigate to={HOME_ROUTE} replace />
                    )
                  }
                />
                {/* S11.1: the five pink reports — tabs are ROUTE-DERIVED
                    (/auth/reports/:report, report = order-margin|back-order|
                    unreceived|receivables|payables). Rides the reports.opsPack
                    flag (the umbrella 報表 flag); the 報表 nav wires all five
                    (navigation.constants.ts). An unknown key — including the
                    retired /auth/reports/ops fixture URL — falls back to the
                    landing report. */}
                <Route
                  path="reports"
                  element={
                    <FeatureGate flag="reports.opsPack">
                      <Navigate to="/auth/reports/order-margin" replace />
                    </FeatureGate>
                  }
                />
                <Route
                  path="reports/:report"
                  element={
                    <FeatureGate flag="reports.opsPack">
                      <ReportsPage />
                    </FeatureGate>
                  }
                />
              </Route>

              {/* Fallback route */}
              <Route path="*" element={<Navigate to={HOME_ROUTE} replace />} />
            </Routes>
            <DevFlagPanel />
            {/* DH1.2: DEMO badge — static string comparison (Vite bakes
                VITE_APP_ENV per mode) so build:api dead-code-eliminates the
                ribbon; bottom-left, below modals, never overlaps the
                DevFlagPanel FAB bottom-right. */}
            {import.meta.env.VITE_APP_ENV === 'demo' && <DemoRibbon />}
          </BrowserRouter>
        </NavigationGuardProvider>
      </FeatureFlagProvider>
    </ThemeProvider>
  );
}

export default App;
